'use client';

import { ChartNoAxesColumn } from 'lucide-react';
import { createContext, memo, useCallback, useContext, useState } from 'react';

import {
  clearAllThunk,
  clearCategoryThunk,
  removeFromComparisonThunk
} from '@screens/Comparison/model/slice/comparisonSlice';
import { ComparisonProducts } from '@screens/Comparison/model/types/comparisonState';
import { ProductList } from '@screens/Comparison/ui/ProductList/ProductList';
import { distributionByCategory } from '@screens/Comparison/utils/distributionByCategory/distributionByCategory';
import { ACCENT } from '@shared/constants/colors';
import { useAppDispatch } from '@shared/hooks/redux';
import { CharacteristicCategoryType } from '@shared/types/characteristics';
import { HttpStatuses } from '@shared/types/enums/http.enum';
import { BreadcrumbItemType, WithNeedUpdateTokenProps } from '@shared/types/global';
import { ProductsType } from '@shared/types/product';
import { PageHead } from '@shared/ui/PageHead';
import { HStack } from '@shared/ui/Stack';
import { Empty } from '@widgets/Empty/Empty';
import { RefreshTokenState } from '@widgets/RefreshTokenState/RefreshTokenState';

import { CategoryTabs } from './CategoryTabs/CategoryTabs';
import cls from './Comparison.module.scss';

type ComparisonProps = {
  comparisonProducts: ProductsType;
  productCharacteristics?: CharacteristicCategoryType[];
} & WithNeedUpdateTokenProps;

type ComparisonContextType = {
  products: ComparisonProducts;
  activeCategory: string;
  fixedSlider: boolean;
  activeSlideIndex: number;
  controlSlider: {
    prev: boolean;
    next: boolean;
  };
  changeActiveCategory: (value: string) => void;
  showFixedSlider: (value: boolean) => void;
  changeSlide: (index: number, prev: boolean, next: boolean) => void;
  clearCategory: (category: string) => Promise<void>;
  removeProduct?: (productId: string, category: string) => Promise<void>;
  productCharacteristics?: CharacteristicCategoryType[];
};

const breadcrumbs: BreadcrumbItemType[] = [
  {
    label: 'Главная',
    url: '/'
  },
  {
    label: 'Сравнение',
    url: ''
  }
];

const initComparisonContext = {
  products: {},
  activeCategory: '',
  activeSlideIndex: 0,
  fixedSlider: false,
  controlSlider: {
    prev: false,
    next: false
  },
  changeActiveCategory: () => {},
  showFixedSlider: () => {},
  changeSlide: () => {},
  clearCategory: async () => {}
};

const ComparisonContext = createContext<ComparisonContextType>(initComparisonContext);

export const Comparison = memo(({ comparisonProducts, productCharacteristics, needUpdateToken }: ComparisonProps) => {
  const dispatch = useAppDispatch();

  const [products, setProducts] = useState<ComparisonContextType['products']>(
    distributionByCategory(comparisonProducts)
  );

  const [activeCategory, setActiveCategory] = useState(Object.keys(products)[0]);
  const [activeSlideIndex, setActiveSlideIndex] = useState(initComparisonContext.activeSlideIndex);
  const [fixedSlider, setFixedSlider] = useState(initComparisonContext.fixedSlider);
  const [controlSlider, setControlSlider] = useState(initComparisonContext.controlSlider);

  const hasProducts = !!Object.keys(products).length;

  const changeActiveCategory = useCallback<ComparisonContextType['changeActiveCategory']>((category) => {
    setActiveCategory(category);
  }, []);

  const showFixedSlider = useCallback<ComparisonContextType['showFixedSlider']>((show) => {
    setFixedSlider(show);
  }, []);

  const changeSlide = useCallback<ComparisonContextType['changeSlide']>((index, prev, next) => {
    setActiveSlideIndex(index);
    setControlSlider({ next, prev });
  }, []);

  const clearCategory = useCallback<ComparisonContextType['clearCategory']>(
    async (category) => {
      const removeIds = Object.keys(products[category]);
      const productIds = Object.keys(comparisonProducts);
      const actualProductIds = productIds.filter((id) => !removeIds.includes(id));

      const { payload } = await dispatch(clearCategoryThunk({ removeIds, actualProductIds, category }));

      if (payload !== HttpStatuses.ERROR) {
        if (activeCategory === category && Object.keys(products).length >= 2) {
          const otherCategories = Object.keys(products).filter((el) => el !== category);
          setActiveCategory(otherCategories[0]);
        }
        if (products[category]) {
          setProducts((prevState) => {
            const newState = { ...prevState };
            delete newState[category];
            return newState;
          });
        }
      }
    },
    [activeCategory, comparisonProducts, dispatch, products]
  );

  const removeProduct = useCallback<NonNullable<ComparisonContextType['removeProduct']>>(
    async (productId, category) => {
      const { payload } = await dispatch(removeFromComparisonThunk(productId));

      if (payload !== HttpStatuses.ERROR) {
        setProducts((prevState) => {
          const newState = { ...prevState };

          if (Object.values(newState[category]).length === 1) {
            delete newState[category];

            const otherCategories = Object.keys(newState);
            if (otherCategories.length) {
              setActiveCategory(otherCategories[0]);
            }
          } else {
            delete products[category][productId];
          }

          return newState;
        });
      }
    },
    [dispatch, products]
  );

  const onClickHandler = async () => {
    const { payload } = await dispatch(clearAllThunk());

    if (payload !== HttpStatuses.ERROR) {
      setProducts({});
    }
  };

  return (
    <>
      <ComparisonContext.Provider
        value={{
          products,
          productCharacteristics,
          activeCategory,
          activeSlideIndex,
          fixedSlider,
          controlSlider,
          changeActiveCategory,
          showFixedSlider,
          changeSlide,
          clearCategory,
          removeProduct
        }}
      >
        <div className={cls.wrap}>
          <PageHead
            breadcrumbs={breadcrumbs}
            title={
              <HStack
                justify={'between'}
                align={'center'}
              >
                <div>Сравнение</div>
                {hasProducts ? (
                  <button
                    className={cls.clear}
                    onClick={onClickHandler}
                    aria-label={'Очистить сравнение'}
                  >
                    Очистить сравнение
                  </button>
                ) : null}
              </HStack>
            }
          />
          {hasProducts ? (
            <>
              <CategoryTabs />
              <ProductList />
            </>
          ) : (
            <Empty
              title={'Нет товаров для сравнения'}
              description={'Добавьте интересующие вас товары в сравнения'}
              icon={<ChartNoAxesColumn color={ACCENT} />}
            />
          )}
        </div>
      </ComparisonContext.Provider>
      <RefreshTokenState
        redirect={false}
        {...{ needUpdateToken }}
      />
    </>
  );
});

export const useComparison = () => useContext(ComparisonContext);
